import React from "react";

import { KindeOrg } from "@inrange/building-manager-api-client/models-organisation";
import { ScoreFactor } from "@inrange/building-manager-api-client/models-site";
import styled from "styled-components";
import { BreadCrumb } from "../components/Breadcrumbs";
import Header from "../components/Header";
import { ScoreTooltipOrgContext } from "../utils/buildScoreTooltipText";
import Sidebar from "../components/Sidebar";
import LeasedSitesInActive from "../images/icons/LeasedSitesInActive.svg";
import OperationalSitesInActive from "../images/icons/OperationalSitesInActive.svg";
import OwnedSitesInActive from "../images/icons/OwnedSitesInActive.svg";
import MapIcon from "../images/icons/map.svg";

interface SidebarWithHeaderProps {
  children: React.ReactNode;
  crumbs: BreadCrumb[];
  currentUrl: string;
  userEmail: string;
  rootPath: string;
  orgType: string;
  siteCounts: Record<string, number>;
  onClickLogout: () => void;
  onOrgSwitch: (id: string) => void;
  userOrgs: KindeOrg[];
  currentKindeOrg: string;
  score?: number;
  scoreFactors?: ScoreFactor[];
  scoreOrgContext: ScoreTooltipOrgContext;
  siteStatus?: string;
  siteOperationalDate?: string | null;
}

const SidebarWithHeader: React.FC<SidebarWithHeaderProps> = ({
  children,
  crumbs,
  currentUrl,
  userEmail,
  rootPath,
  orgType,
  siteCounts,
  onClickLogout,
  onOrgSwitch,
  userOrgs,
  currentKindeOrg,
  score,
  scoreFactors,
  scoreOrgContext,
  siteStatus,
  siteOperationalDate,
}) => {
  const landlordCount = siteCounts.landlord || 0;
  const ownerOccupierCount = siteCounts.ownerOccupier || 0;
  const tenantCount = siteCounts.tenant || 0;
  const operationalCount = siteCounts.operational || 0;

  const sidebarLinks: {
    label: string;
    icon: string;
    url: string;
  }[] = [];

  if (orgType === "Tenant") {
    if (ownerOccupierCount > 0) {
      const isOwnerOccupierOnly = tenantCount === 0 && ownerOccupierCount > 0;
      sidebarLinks.push({
        label: isOwnerOccupierOnly ? "Sites" : "Owned Sites",
        icon: OwnedSitesInActive,
        url: isOwnerOccupierOnly
          ? `${rootPath}dashboard`
          : `${rootPath}sites/ownerOccupier`,
      });
    }
    if (tenantCount > 0) {
      const isTenantOnly = tenantCount > 0 && ownerOccupierCount === 0;
      sidebarLinks.push({
        label: isTenantOnly ? "Sites" : "Leased Sites",
        icon: LeasedSitesInActive,
        url: isTenantOnly ? `${rootPath}dashboard` : `${rootPath}sites/tenant`,
      });
    }
  } else if (orgType === "Landlord") {
    if (landlordCount + ownerOccupierCount > 0) {
      sidebarLinks.push({
        label: "Sites",
        icon: OwnedSitesInActive,
        url: `${rootPath}dashboard`,
      });
    }
  } else if (orgType === "Funder") {
    sidebarLinks.push({
      label: "SPVs",
      icon: OperationalSitesInActive,
      url: `${rootPath}dashboard`,
    });
  }
  if (orgType !== "Funder") {
    if (operationalCount > 0) {
      sidebarLinks.push({
        label: "Operational Sites",
        icon: OperationalSitesInActive,
        url: `${rootPath}sites/operational`,
      });
    }
    sidebarLinks.push({
      label: "Marketplace",
      icon: MapIcon,
      url: `${rootPath}marketplace`,
    });
  }

  return (
    <Container>
      <FixedSidebar>
        <Sidebar
          currentUrl={currentUrl}
          links={sidebarLinks}
          onClickLogout={onClickLogout}
          onOrgSwitch={onOrgSwitch}
          userOrgs={userOrgs}
          currentKindeOrg={currentKindeOrg}
        />
      </FixedSidebar>
      <MainContainer>
        <Header
          crumbs={crumbs}
          userEmail={userEmail}
          score={score}
          scoreFactors={scoreFactors}
          scoreOrgContext={scoreOrgContext}
          siteStatus={siteStatus}
          siteOperationalDate={siteOperationalDate}
        />
        <ChildContainer>{children}</ChildContainer>
      </MainContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 0;
  margin: 0;
  background: ${({ theme }) => theme.colors.grey100};
  height: 100vh;
`;

const MainContainer = styled.div`
  background: ${({ theme }) => theme.colors.grey100};
  margin: 0 0 0 100px;
  padding: 0 0 50px 0;
`;

const ChildContainer = styled.div`
  border-top: 5px solid ${({ theme }) => theme.colors.grey100};
  margin: 0;
  padding: 0;
`;

export default SidebarWithHeader;

const FixedSidebar = styled.div`
  background: white;
  border-right: 5px solid ${({ theme }) => theme.colors.grey100};
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  overflow-x: hidden;
`;
